import { getBooleanEnvValue } from './env.utils'

/**
 * Master kill-switch for the ENTIRE SVD (Smart Variant Discovery) program. BOTH surfaces gate on it,
 * on top of the shared `ramp_svd-rollout-web` experiment:
 *  - the product-card flow (server `getServerSideBaseProps` → `svd.config` re-export → `shouldUseSvdCard`)
 *  - the PDP buy-box flow (`useIsSvdEnabled`)
 *
 * Env-driven via `NEXT_PUBLIC_ENABLE_SVD_FLOW_DISCOVERY` — **true in dev and pre-integ, false everywhere
 * else** (prod-safe default `false`). While off, both surfaces stay dark regardless of the experiment bucket
 * (card: server skips the eval; PDP: `useIsSvdEnabled` returns false before the Statsig call). Per-env
 * values live in `.helm/values-*.yaml` (+ `.env` / `.env.local` for local dev).
 *
 * Reads only `env.utils` ON PURPOSE, so it stays dependency-free / client-safe: it's imported by both
 * client (PDP hook, card render path) and server code and must never drag in the statsig registry.
 */
export const ENABLE_SVD_FLOW_DISCOVERY = getBooleanEnvValue(process.env.NEXT_PUBLIC_ENABLE_SVD_FLOW_DISCOVERY, false, {
  required: false,
})

/**
 * TEMPORARY (STRFRNT-15810 · CLP/Home SVD preview) — header that asks PL for SVD-shaped cards.
 *
 * For SVD-flow users we send this, unchanged host/endpoint, on every PL call site that can render a
 * product card on CLP or Home, so the response carries SVD-shaped cards (`anchorAttributes` /
 * `selectedAttributes` / `displayConfig` / `spId`) — letting us exercise the SVD product card on real PL
 * data. Deliberately excluded from PDP (owns its own SVD integration). Wired call sites, all gated on
 * `isSvdFlowEnabled`:
 *  - CLP + Home `layouts/fetch` (SSR) — `getCollectionPageDataPL`, `fetchSimplePLLayout`
 *  - CLP client `widgets/fetch` (single-widget pagination) — `fetch-widget-data.ts`
 *  - Layout-level "load more widgets" pagination — `fetch-layout-data.ts` (excludes PDP explicitly via
 *    `svdEnrichHeaderEnabled && this.pageType !== ProtoPageType.PDP`, since PDP paginates through the
 *    same shared `LayoutDataFetchingService`)
 *  - CLP filter/sort refresh — `use-clp-filters.ts` → `fetch-layout-data.ts`
 *
 * Verified live on pre-integ (2026-08-07): the header is a no-op there today (that PL build already
 * emits SVD shape unconditionally) but is sent anyway per PL's ask, ready for environments/behavior
 * where it does gate the shape.
 *
 * Also sent on every search PL fetch (`useSearchResults.ts` — initial/filtered `layouts/fetch`, filter-refresh
 * and load-more `widgets/fetch-data`), gated on `useIsSvdEnabled()`: the same header enrichment carries the
 * Algolia `searchObjectId` attribution field on `products[i].responseContext` (STRFRNT-16013) that the
 * search grid's `isPlSvdCard` branch renders `SvdProductCard` off of.
 *
 * Delete alongside every `isPlSvdCard`/`mapPlSvdCard` call site (and every one of the wired sites above)
 * once every PL environment emits the canonical SVD shape unconditionally and neither surface needs a
 * shape-guard fallback to the legacy card.
 */
// eslint-disable-next-line i18next/no-literal-string -- HTTP header name, not user-facing copy
export const SVD_ENRICH_VERSION_HEADER = 'x-enrich-version'
// eslint-disable-next-line i18next/no-literal-string -- HTTP header value, not user-facing copy
export const SVD_ENRICH_VERSION_VALUE = 'V2'
